import { ValueOf } from 'type-fest'
import { sendToSentry } from 'ComponentLibrary'

export const LOCAL_STORAGE_KEYS = {
    SELECTED_CUSTOMER: 'selectedCustomer',
    ORDER_LIST_SORT_OPTION: 'orderListSortOption',
} as const
export type Key = ValueOf<typeof LOCAL_STORAGE_KEYS>

export function writeToLocalStorage<TValue>(key: Key, value: TValue): void {
    try {
        localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
        if (error instanceof DOMException) {
            const message = ['QuotaExceededError', 'NS_ERROR_DOM_QUOTA_REACHED'].includes(error.name)
                ? 'Local storage is full'
                : error.message
            throw new LocalStorageUnavailableError(message)
        }
        sendToSentry(error)
    }
}

export function readFromLocalStorage<TValue>(key: Key): TValue | undefined {
    const value = localStorage.getItem(key)
    if (value === null || ['undefined', 'null'].includes(value)) {
        return undefined
    }
    try {
        return JSON.parse(value)
    } catch {
        throw new LocalStorageReadError(`Couldn't parse value from local storage: ${key} - ${value}`)
    }
}

export function removeFromLocalStorage(key: Key): void {
    try {
        localStorage.removeItem(key)
    } catch (error) {
        sendToSentry(error)
    }
}

export function clearLocalStorage(): void {
    Object.keys(LOCAL_STORAGE_KEYS).forEach((key) => removeFromLocalStorage(key as Key))
}

class LocalStorageUnavailableError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'LocalStorageUnavailableError'
    }
}

class LocalStorageReadError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'LocalStorageReadError'
    }
}
